import { useEffect, useRef, useState } from 'react'
import { AlertCircle, Bug, CheckCircle2, FileText, Paperclip, ShieldCheck, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button, Modal } from '../../components/ui'
import { useAuthStore } from '../../stores/authStore'
import { collectClientContext } from './clientDiagnostics'
import { workItemsAPI } from './api'
import type { WorkItem } from './types'
import { cn } from '../../lib/cn'

const DRAFT_KEY = 'pim.work-items.bug-report-draft'
const MAX_FILES = 5
const MAX_FILE_SIZE = 10 * 1024 * 1024
const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'pdf', 'txt', 'log']

interface BugReportModalProps {
  isOpen: boolean
  onClose: () => void
}
function createIdempotencyKey() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function getErrorMessage(error: any): string {
  return error?.response?.data?.error?.message || error?.message || 'Не удалось отправить отчёт'
}

export default function BugReportModal({ isOpen, onClose }: BugReportModalProps) {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [title, setTitle] = useState('')
  const [details, setDetails] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [idempotencyKey, setIdempotencyKey] = useState(createIdempotencyKey)
  const [dragActive, setDragActive] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [fileError, setFileError] = useState('')
  const [created, setCreated] = useState<(WorkItem & { report_id?: number }) | null>(null)
  const [attachmentWarning, setAttachmentWarning] = useState('')

  useEffect(() => {
    if (!isOpen || created) return
    try {
      const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}')
      setTitle(typeof draft.title === 'string' ? draft.title : '')
      setDetails(typeof draft.details === 'string' ? draft.details : '')
      setIdempotencyKey(
        typeof draft.idempotencyKey === 'string' ? draft.idempotencyKey : createIdempotencyKey(),
      )
    } catch {
      setIdempotencyKey(createIdempotencyKey())
    }
  }, [isOpen, created])

  useEffect(() => {
    if (!isOpen || created) return
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ title, details, idempotencyKey }))
    } catch {}
  }, [title, details, idempotencyKey, isOpen, created])

  useEffect(() => {
    if (!isOpen) return
    const onPaste = (event: ClipboardEvent) => {
      const pasted = Array.from(event.clipboardData?.files || [])
      if (pasted.length) addFiles(pasted)
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  })

  const addFiles = (incoming: File[]) => {
    setFileError('')
    const next: File[] = []
    for (const file of incoming) {
      const extension = file.name.split('.').pop()?.toLowerCase() || ''
      if (!ALLOWED_EXTENSIONS.includes(extension)) {
        setFileError('Разрешены только PNG, JPG, WebP, PDF, TXT и LOG')
        continue
      }
      if (file.size > MAX_FILE_SIZE) {
        setFileError(`Файл «${file.name}» больше 10 МБ`)
        continue
      }
      next.push(file)
    }
    setFiles((current) => {
      const deduplicated = [...current]
      for (const file of next) {
        if (!deduplicated.some((item) => item.name === file.name && item.size === file.size)) {
          deduplicated.push(file)
        }
      }
      if (deduplicated.length > MAX_FILES) {
        setFileError('Можно приложить не более 5 файлов')
      }
      return deduplicated.slice(0, MAX_FILES)
    })
  }

  const resetAndClose = () => {
    if (submitting) return
    setTitle('')
    setDetails('')
    setFiles([])
    setError('')
    setFileError('')
    setCreated(null)
    setAttachmentWarning('')
    setIdempotencyKey(createIdempotencyKey())
    try {
      localStorage.removeItem(DRAFT_KEY)
    } catch {}
    onClose()
  }

  const submit = async () => {
    const normalizedTitle = title.trim()
    if (!normalizedTitle) {
      setError('Кратко опишите, что сломалось')
      return
    }
    setSubmitting(true)
    setError('')
    setAttachmentWarning('')
    try {
      const response = await workItemsAPI.createReport({
        title: normalizedTitle,
        details: details.trim() || undefined,
        sourcePageUrl: window.location.href,
        sourcePageTitle: document.title,
        clientContext: collectClientContext(),
        idempotencyKey,
      })
      const item = response.data.data
      setCreated(item)
      try {
        localStorage.removeItem(DRAFT_KEY)
      } catch {}
      if (files.length) {
        try {
          await workItemsAPI.uploadAttachments(item.id, item.report_id, files)
        } catch (uploadError) {
          setAttachmentWarning(
            `Обращение создано, но не все файлы загрузились: ${getErrorMessage(uploadError)}`,
          )
        }
      }
    } catch (submitError) {
      setError(getErrorMessage(submitError))
    } finally {
      setSubmitting(false)
    }
  }

  const canOpenBacklog = user?.role === 'admin' || user?.role === 'owner'
  const canSeeOwnReports = (user?.permissions ?? []).includes('Backlog:READ_OWN')

  if (created) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={resetAndClose}
        title="Отчёт отправлен"
        size="md"
        closeOnOverlayClick={false}
        footer={
          <>
            {canOpenBacklog && (
              <Button
                variant="secondary"
                onClick={() => {
                  navigate(`/settings/backlog?item=${created.id}`)
                  resetAndClose()
                }}
              >
                Открыть в беклоге
              </Button>
            )}
            {canSeeOwnReports && (
              <Button
                variant="secondary"
                onClick={() => {
                  navigate('/backlog/mine')
                  resetAndClose()
                }}
              >
                Мои обращения
              </Button>
            )}
            <Button onClick={resetAndClose}>Готово</Button>
          </>
        }
      >
        <div className="flex gap-4">
          <div className="h-11 w-11 shrink-0 rounded-full bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300 flex items-center justify-center">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-gray-700 dark:text-zinc-300">
              Обращение <span className="font-semibold text-gray-900 dark:text-zinc-100">{created.item_key}</span>{' '}
              попало во входящие. Команда увидит технический контекст и свяжется с вами, если понадобятся детали.
            </p>
            <p className="mt-2 font-medium text-gray-900 dark:text-zinc-100">{created.title}</p>
            {attachmentWarning && (
              <div className="mt-3 rounded-md bg-yellow-50 border border-yellow-200 p-3 text-xs text-yellow-800 dark:bg-yellow-950/40 dark:border-yellow-900 dark:text-yellow-200">
                {attachmentWarning}
              </div>
            )}
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title="Сообщить об ошибке"
      subtitle="Отчёт попадёт во входящие команды. URL страницы и техническая информация прикрепятся автоматически."
      size="md"
      closeOnOverlayClick={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={resetAndClose} disabled={submitting}>
            Отмена
          </Button>
          <Button
            onClick={submit}
            loading={submitting}
            disabled={!title.trim()}
            icon={<Bug className="h-4 w-4" />}
          >
            Отправить отчёт
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-zinc-400 mb-1.5">
            Заголовок
          </span>
          <input
            autoFocus
            value={title}
            maxLength={200}
            onChange={(event) => {
              setTitle(event.target.value)
              setError('')
            }}
            placeholder="Кратко: что сломалось?"
            className={cn(
              'w-full h-11 rounded-md border bg-white px-3 text-sm text-gray-900 outline-none transition',
              'placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500',
              'dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder:text-zinc-500',
              error && !title.trim() ? 'border-red-400' : 'border-gray-300 dark:border-zinc-700',
            )}
          />
        </label>

        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-zinc-400 mb-1.5">
            Что вы делали? <span className="font-normal">(необязательно)</span>
          </span>
          <textarea
            value={details}
            maxLength={20000}
            onChange={(event) => setDetails(event.target.value)}
            placeholder="Шаги воспроизведения, ожидаемое и фактическое поведение…"
            rows={4}
            className="w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder:text-zinc-500"
          />
        </label>

        <div>
          <span className="block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-zinc-400 mb-1.5">
            Скриншот или файл <span className="font-normal">(необязательно)</span>
          </span>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".png,.jpg,.jpeg,.webp,.pdf,.txt,.log"
            className="hidden"
            onChange={(event) => addFiles(Array.from(event.target.files || []))}
          />
          <div
            className={cn(
              'rounded-lg border border-dashed p-3 transition-colors',
              dragActive
                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30'
                : 'border-gray-300 bg-gray-50/60 dark:border-zinc-700 dark:bg-zinc-950/40',
            )}
            onDragEnter={(event) => {
              event.preventDefault()
              setDragActive(true)
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragActive(false)}
            onDrop={(event) => {
              event.preventDefault()
              setDragActive(false)
              addFiles(Array.from(event.dataTransfer.files))
            }}
          >
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-white border border-gray-200 text-gray-500 dark:bg-zinc-900 dark:border-zinc-700 dark:text-zinc-400 flex items-center justify-center">
                <Paperclip className="h-4 w-4" />
              </div>
              <p className="flex-1 text-xs text-gray-500 dark:text-zinc-400">
                Перетащите, вставьте из буфера или выберите файл. До 5 файлов по 10 МБ.
              </p>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
              >
                Прикрепить
              </Button>
            </div>
          </div>
          {fileError && <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">{fileError}</p>}
          {files.length > 0 && (
            <ul className="mt-2 space-y-1.5">
              {files.map((file, index) => (
                <li
                  key={`${file.name}-${file.size}`}
                  className="flex items-center gap-2 rounded-md border border-gray-200 px-2.5 py-2 text-xs dark:border-zinc-800"
                >
                  <FileText className="h-4 w-4 text-gray-400 shrink-0" />
                  <span className="flex-1 truncate text-gray-700 dark:text-zinc-300">{file.name}</span>
                  <span className="text-gray-400">{(file.size / 1024 / 1024).toFixed(1)} МБ</span>
                  <button
                    type="button"
                    onClick={() => setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                    className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                    aria-label={`Удалить ${file.name}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-start gap-2 rounded-md bg-gray-50 px-3 py-2.5 text-xs text-gray-500 dark:bg-zinc-950/60 dark:text-zinc-400">
          <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5 text-green-600 dark:text-green-400" />
          В отчёт попадут адрес страницы, версия приложения, браузер и последние технические ошибки.
          Пароли, токены и содержимое форм не собираются.
        </div>
      </div>
    </Modal>
  )
}
